import OwTitle from "@components/shared/OwTitle/OwTitle";
import UiContext from "@contexts/UiContext";
import { useContext, useEffect, useMemo, useState } from "react";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";
import ICustomImport from "@models/ICustomImport";
import IUiSocketSetup from "@models/IUiSocketSetup";
import EndpointsEnum from "@models/EndpointsEnum";
import IOnGetCustomImportsDTO from "@models/DTO/IOnGetCustomImportsDTO";
import IGetCustomImportsDTO from "@models/DTO/IGetCustomImportsDTO";
import OwTable from "@components/shared/OwTable/OwTable";
import IOwTableCols from "@models/IOwTableCols";
import OwButton from "@components/shared/OwButton/OwButton";
import { CustomImportSidebar } from "./CustomImportSidebar/CustomImportSidebar";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import ICustomImportUI from "@models/ICustomImportUI";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import IOnDeleteCustomImportDTO from "@models/DTO/IOnDeleteCustomImportDTO";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import INotification from "@models/INotification";

type Props = {};

export const CustomImports = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const renderActionsCol = (row: ICustomImportUI, rowIndex: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onEditClickFn={() => {
                        showCustomImportSidebar(row);
                    }}
                    onTrashClickFn={() => {
                        setDeleteRow(row);
                        setDeleteRowElem(`row-${rowIndex}`);
                    }}
                />
            </div>
        );
    };

    const [deleteRow, setDeleteRow] = useState<ICustomImportUI | null>(null);
    const [deleteRowElem, setDeleteRowElem] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [initialData, setInitialData] = useState(true);
    const [customImports, setCustomImports] = useState<ICustomImportUI[]>([]);
    const columns: IOwTableCols[] = useMemo(() => {
        return [
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                renderFn: renderActionsCol,
                renderFnEmitsRow: true,
                className: "w-8r",
            },
            {
                title: "name",
                valueProp: "name",
                className: "w-15r",
            },
            {
                title: "type",
                valueProp: "type",
                className: "w-10r",
            },
            {
                title: "has_header_row",
                valueProp: "has_header_row",
                className: "text-center w-10r",
                renderFn(value, rowIndex, isDetailViewTable, parentRowIndex) {
                    return <i className={value ? "fad fa-check-circle text-success" : "fad fa-times-circle text-danger"} />;
                },
            },
            {
                title: "import_cols_count",
                valueProp: "import_cols",
                renderFnEmitsRow: true,
                renderFn(row: ICustomImportUI) {
                    return <div className="text-center">{row.import_cols.length}</div>;
                },
                className: "w-10r",
            },
            {
                title: "import_steps_count",
                valueProp: "import_steps",
                renderFnEmitsRow: true,
                renderFn(row: ICustomImportUI) {
                    return <div className="text-center">{row.import_steps.length}</div>;
                },
                className: "w-10r",
            },
            {
                title: "created_at",
                valueProp: "created_at",
                className: "w-10r text-end",
                renderFnEmitsRow: true,
                renderFn(row: ICustomImportUI) {
                    return <div className="text-end">{row.created_at?.toLocaleDateString() || ""}</div>;
                },
            },
            {
                title: "updated_at",
                valueProp: "updated_at",
                className: "w-10r text-end",
                renderFnEmitsRow: true,
                renderFn(row: ICustomImportUI) {
                    return <div className="text-end">{row.updated_at?.toLocaleDateString() || ""}</div>;
                },
            },
        ] as IOwTableCols[];
    }, []);

    const getCustomImports = () => {
        const payload: IGetCustomImportsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CustomImportGetCustomImports, payload);
        setLoading(true);
    };

    const onGetCustomImports = (data: IOnGetCustomImportsDTO) => {
        const processedImports = data.customImports.map((_import) => {
            return {
                ..._import,
                created_at: new Date(_import.created_at),
                updated_at: new Date(_import.updated_at),
            } as ICustomImportUI;
        });

        setCustomImports(processedImports);
        setInitialData(false);
        setLoading(false);
    };

    const deleteCustomImport = (row: ICustomImportUI) => {
        const payload: IOnDeleteCustomImportDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            customImportGuid: row.guid,
        };

        socket.emit(EndpointsEnum.CustomImportDeleteCustomImport, payload);
    };

    const onDeleteCustomImport = (data: IOnDeleteCustomImportDTO) => {
        const notification: INotification = {
            message: "custom_import_deleted_successfully",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setDeleteRow(null);
        setDeleteRowElem(null);

        getCustomImports();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CustomImportOnGetCustomImports, fn: onGetCustomImports },
        { endpoint: EndpointsEnum.CustomImportOnDeleteCustomImport, fn: onDeleteCustomImport },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        getCustomImports();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const closeSidebar = (refresh?: boolean) => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);

        if (refresh) {
            getCustomImports();
        }
    };

    const showCustomImportSidebar = (row?: ICustomImportUI) => {
        const sidebarComponent = <CustomImportSidebar closeFn={closeSidebar} existingCustomImport={row} />;

        uiContext.setSidebarComponent(sidebarComponent);
        uiContext.toggleSidebar();
    };

    const confirmDelete = () => {
        if (deleteRow) {
            deleteCustomImport(deleteRow);
        }
    };

    const cancelDelete = () => {
        setDeleteRow(null);
        setDeleteRowElem(null);
    };

    return (
        <div className={"CustomImports"}>
            <div className="d-flex align-items-center mt-2">
                <OwTitle title={"custom_imports"} leftIconName={"fad fa-file-import"} />

                <div className="ms-2">
                    <OwButton
                        className="btn-warning"
                        title="create"
                        leftIconClass="fad fa-plus"
                        fn={() => {
                            showCustomImportSidebar();
                        }}
                    />
                </div>
            </div>

            <div className="table-container mt-2 position-relative">
                <ConfirmDeleteOverlayFn
                    targetElem={deleteRowElem}
                    handleConfirm={confirmDelete}
                    handleCancel={cancelDelete}
                    skipMainView={true}
                    nestedParentElem="custom-imports-table"
                />
                <OwFnTable
                    id="custom-imports-table"
                    columns={columns}
                    dataSet={customImports}
                    initialData={initialData}
                    dataFetch={loading}
                />
            </div>
        </div>
    );
};
